// B2B Allgemeine Geschäftsbedingungen (AGB) Template Data
// SaaS-Lizenzvertrag für institutionelle Kunden — konsolidierte Fassung (11 §§)

export interface AGBContractParty {
  name: string;
  address: string;
  representedBy: string;
}

export interface AGBServiceScope {
  seats: number;
  apiAccess: boolean;
  apiRateLimit: string;
  creditsPerMonth: string;
  customFeatures: string[];
}

export type BillingModel = 'monthly' | 'yearly' | 'one_time_per_user' | 'credits_plus_fixed' | 'cooperation';

export interface AGBCommercialTerms {
  pricing: string;
  paymentTermDays: number;
  billingModel: BillingModel;
  pricePerUser: string;
  fixedFee: string;
  creditsIncluded: string;
  cooperationCostBasis: string;
}

export interface AGBContractTerms {
  startDate: string;
  duration: string;
  noticePeriod: string;
  slaUptime: string;
  supportLevel: 'email' | 'phone' | 'dedicated';
  liabilityCapType: 'annual_value' | 'fixed';
  liabilityCap: string;
  ndaLevel: 'standard' | 'extended';
  mainContractReference: string;
  autoRenewal: boolean;
  fixedEndDate: string;
  tenantIsolation: boolean;
  customReviewCriteria: boolean;
  customCriteriaIP: 'client_owned' | 'shared' | 'paperverse_owned';
  activeConsentForChanges: boolean;
}

export interface SavedAGB {
  id: string;
  customer: AGBContractParty;
  serviceScope: AGBServiceScope;
  commercialTerms: AGBCommercialTerms;
  contractTerms: AGBContractTerms;
  createdAt: string;
  updatedAt: string;
  status: 'draft' | 'finalized';
}

export const AGB_STORAGE_KEY = 'paperverse_saved_agbs';

export const agbProvider = {
  name: 'PaperVerse UG (haftungsbeschränkt)',
  address: 'Moritzburger Straße 39, 01127 Dresden',
  representedBy: 'Oliver Mey, Geschäftsführer',
  registrationInfo: 'Amtsgericht Dresden, HRB [Nummer]',
  email: 'contact@paperverse.io',
};

export const defaultAGBCustomer: AGBContractParty = {
  name: '',
  address: '',
  representedBy: '',
};

export const defaultAGBServiceScope: AGBServiceScope = {
  seats: 10,
  apiAccess: true,
  apiRateLimit: '1000 Requests/Stunde',
  creditsPerMonth: '50',
  customFeatures: [],
};

export const defaultAGBCommercialTerms: AGBCommercialTerms = {
  pricing: '',
  paymentTermDays: 30,
  billingModel: 'yearly',
  pricePerUser: '',
  fixedFee: '',
  creditsIncluded: '',
  cooperationCostBasis: '',
};

export const defaultAGBContractTerms: AGBContractTerms = {
  startDate: '',
  duration: '12 Monate',
  noticePeriod: '3 Monate zum Quartalsende',
  slaUptime: '99,5%',
  supportLevel: 'email',
  liabilityCapType: 'annual_value',
  liabilityCap: '',
  ndaLevel: 'standard',
  mainContractReference: '',
  autoRenewal: true,
  fixedEndDate: '',
  tenantIsolation: false,
  customReviewCriteria: false,
  customCriteriaIP: 'shared',
  activeConsentForChanges: false,
};

export interface AGBClause {
  id: number;
  title: string;
  content: string;
  /** If set, clause is only included when the condition returns true */
  condition?: (agb: SavedAGB) => boolean;
}

// ── Billing-model helpers ──────────────────────────────────────────

function billingModelLabel(model: BillingModel): string {
  switch (model) {
    case 'monthly': return 'monatlich';
    case 'yearly': return 'jährlich';
    case 'one_time_per_user': return 'einmalig pro Nutzer';
    case 'credits_plus_fixed': return 'Credits + Fixbetrag';
    case 'cooperation': return 'Kooperationsmodell (technol. Kosten)';
  }
}

function customCriteriaIPLabel(ip: AGBContractTerms['customCriteriaIP']): string {
  switch (ip) {
    case 'client_owned': return 'dem Kunden';
    case 'shared': return 'beiden Parteien gemeinsam';
    case 'paperverse_owned': return 'dem Anbieter';
  }
}

/** Helper to replace template placeholders */
export function resolveAGBPlaceholders(text: string, agb: SavedAGB): string {
  const ct = agb.contractTerms;
  const cm = agb.commercialTerms;

  const replacements: Record<string, string> = {
    '{{customer.name}}': agb.customer.name || '[Kundenname]',
    '{{customer.address}}': agb.customer.address || '[Kundenadresse]',
    '{{customer.representedBy}}': agb.customer.representedBy || '[Vertretungsberechtigte Person]',
    '{{provider.name}}': agbProvider.name,
    '{{provider.address}}': agbProvider.address,
    '{{provider.representedBy}}': agbProvider.representedBy,
    '{{provider.registrationInfo}}': agbProvider.registrationInfo,
    '{{provider.email}}': agbProvider.email,
    '{{serviceScope.seats}}': String(agb.serviceScope.seats),
    '{{serviceScope.apiRateLimit}}': agb.serviceScope.apiRateLimit || '[Rate-Limit]',
    '{{serviceScope.creditsPerMonth}}': agb.serviceScope.creditsPerMonth || '[Credits]',
    '{{commercialTerms.pricing}}': cm.pricing || '[Vergütung]',
    '{{commercialTerms.paymentTermDays}}': String(cm.paymentTermDays),
    '{{commercialTerms.billingModel}}': billingModelLabel(cm.billingModel),
    '{{commercialTerms.pricePerUser}}': cm.pricePerUser || '[Preis pro Nutzer]',
    '{{commercialTerms.fixedFee}}': cm.fixedFee || '[Fixbetrag]',
    '{{commercialTerms.creditsIncluded}}': cm.creditsIncluded || '[Credits inkl.]',
    '{{commercialTerms.cooperationCostBasis}}': cm.cooperationCostBasis || '[Kostenbasis]',
    '{{contractTerms.startDate}}': ct.startDate || '[Datum]',
    '{{contractTerms.duration}}': ct.duration || '[Laufzeit]',
    '{{contractTerms.noticePeriod}}': ct.noticePeriod || '[Kündigungsfrist]',
    '{{contractTerms.fixedEndDate}}': ct.fixedEndDate || '[Enddatum]',
    '{{contractTerms.slaUptime}}': ct.slaUptime || '[SLA]',
    '{{contractTerms.supportLevel}}': ct.supportLevel === 'email' ? 'E-Mail-Support' : ct.supportLevel === 'phone' ? 'Telefon-Support' : 'Dedizierter Ansprechpartner',
    '{{contractTerms.liabilityCap}}': ct.liabilityCap || (ct.liabilityCapType === 'annual_value' ? 'den jährlichen Vertragswert' : '[Betrag]'),
    '{{contractTerms.ndaLevel}}': ct.ndaLevel === 'extended' ? 'erweiterter' : 'standardmäßiger',
    '{{contractTerms.mainContractReference}}': ct.mainContractReference || 'dem zwischen den Parteien geschlossenen Kooperationsvertrag',
    '{{contractTerms.customCriteriaIP}}': customCriteriaIPLabel(ct.customCriteriaIP),
  };

  let result = text;
  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.split(placeholder).join(value);
  }
  return result;
}

// ── 11 consolidated clauses ────────────────────────────────────────

export const agbClauses: AGBClause[] = [
  // ── § 1 Geltungsbereich ──
  {
    id: 1,
    title: '§ 1 Geltungsbereich',
    content: `(1) Diese Allgemeinen Geschäftsbedingungen (nachfolgend „AGB") gelten für alle Verträge zwischen {{provider.name}}, {{provider.address}}, vertreten durch {{provider.representedBy}} (nachfolgend „Anbieter"), und {{customer.name}}, {{customer.address}}, vertreten durch {{customer.representedBy}} (nachfolgend „Kunde"), über die Nutzung der SaaS-Plattform „PaperVerse" sowie zugehöriger API-Dienste.

(2) Abweichende, entgegenstehende oder ergänzende Geschäftsbedingungen des Kunden werden nur dann Vertragsbestandteil, wenn der Anbieter ihrer Geltung ausdrücklich schriftlich zugestimmt hat. Dies gilt auch, wenn der Anbieter in Kenntnis abweichender Bedingungen des Kunden die Leistung vorbehaltlos erbringt.

(3) Diese AGB ergänzen {{contractTerms.mainContractReference}} (nachfolgend „Hauptvertrag"). Bei Widersprüchen zwischen diesen AGB und dem Hauptvertrag gehen die Regelungen des Hauptvertrags vor.`,
  },

  // ── § 2 Vertragsgegenstand und Leistungsumfang (merged §2+§4) ──
  {
    id: 2,
    title: '§ 2 Vertragsgegenstand und Leistungsumfang',
    content: `(1) Der Anbieter stellt dem Kunden die webbasierte Plattform „PaperVerse" als Software-as-a-Service (SaaS) zur Verfügung. Die Plattform ermöglicht:
a) Hosting und Verwaltung wissenschaftlicher Publikationen
b) KI-gestützte Analyse und Review-Unterstützung
c) Peer-Review-Management
d) Suchmaschinenindexierung wissenschaftlicher Arbeiten
e) API-Zugang für programmatische Integration

(2) Im Einzelnen stellt der Anbieter folgende Leistungen bereit:
a) **Nutzerlizenzen**: {{serviceScope.seats}} Nutzerplätze (Seats) für die Plattform
b) **KI-Review-Credits**: {{serviceScope.creditsPerMonth}} Credits pro Monat für KI-gestützte Analysen
c) **API-Zugang**: Programmatischer Zugang mit einem Rate-Limit von {{serviceScope.apiRateLimit}}
d) **Support**: {{contractTerms.supportLevel}} während der üblichen Geschäftszeiten (Mo–Fr, 9:00–17:00 Uhr MEZ)

(3) Der Anbieter gewährleistet eine Verfügbarkeit der Plattform von mindestens {{contractTerms.slaUptime}} im Jahresmittel. Geplante Wartungsarbeiten werden dem Kunden mindestens 48 Stunden im Voraus per E-Mail angekündigt und finden nach Möglichkeit außerhalb der üblichen Geschäftszeiten statt. Geplante Wartungszeiten bleiben bei der Berechnung der Verfügbarkeit unberücksichtigt.

(4) Der Anbieter erbringt seine Leistungen nach dem jeweiligen Stand der Technik. Weiterentwicklungen und Verbesserungen der Plattform sind dem Anbieter vorbehalten, sofern der vereinbarte Funktionsumfang nicht wesentlich eingeschränkt wird. Über wesentliche Änderungen wird der Kunde mindestens 30 Tage im Voraus informiert.`,
  },

  // ── § 3 Vertragsschluss und Laufzeit ──
  {
    id: 3,
    title: '§ 3 Vertragsschluss und Laufzeit',
    content: `(1) Der Vertrag kommt durch Unterzeichnung des Hauptvertrags oder einer separaten Auftragsbestätigung durch beide Parteien zustande.

(2) Der Vertrag beginnt am {{contractTerms.startDate}} und hat eine Laufzeit von {{contractTerms.duration}}.

(3) Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger Grund liegt insbesondere vor, wenn:
a) die andere Partei wesentliche Vertragspflichten trotz Abmahnung und angemessener Nachfrist nicht erfüllt
b) über das Vermögen der anderen Partei ein Insolvenzverfahren eröffnet oder dessen Eröffnung mangels Masse abgelehnt wird`,
  },

  // ── § 3 Abs. 3a — auto-renewal (conditional) ──
  // We handle this inline: the clause content differs based on autoRenewal.
  // The placeholder resolver + condition approach won't work for mid-clause swaps,
  // so we embed both variants and the resolver picks the right one below in a helper.

  // ── § 4 Mandantentrennung und Datenhoheit (conditional) ──
  {
    id: 4,
    title: '§ 4 Mandantentrennung und Datenhoheit',
    condition: (agb) => agb.contractTerms.tenantIsolation,
    content: `(1) Der Anbieter stellt dem Kunden einen logisch getrennten, privaten Bereich innerhalb der Plattform zur Verfügung (nachfolgend „Mandantenbereich"). Dieser umfasst:
a) Ein eigenes Dashboard für die Verwaltung und Einsicht von Reviews
b) Separate Datenhaltung, die logisch von den Daten anderer Kunden getrennt ist
c) Eigene Nutzerverwaltung innerhalb des zugewiesenen Mandantenbereichs

(2) Der Anbieter gewährleistet, dass Daten des Kunden nicht mit Daten anderer Mandanten vermischt werden und kein Zugriff durch andere Mandanten möglich ist.

(3) Die technische Umsetzung der Mandantentrennung erfolgt durch logische Datenisolierung auf Anwendungsebene. Der Anbieter dokumentiert die hierfür getroffenen technischen Maßnahmen auf Anfrage.

(4) Das Recht des Kunden auf Datenherausgabe und -löschung gemäß § 11 Abs. 1 dieses Vertrags bleibt hiervon unberührt und erstreckt sich auf den gesamten Mandantenbereich.`,
  },

  // ── § 5 Pflichten des Kunden ──
  {
    id: 5,
    title: '§ 5 Pflichten des Kunden',
    content: `(1) Der Kunde ist verpflichtet:
a) Zugangsdaten vertraulich zu behandeln und nicht an unberechtigte Dritte weiterzugeben
b) die Plattform nur für den vereinbarten Zweck zu nutzen
c) geltendes Recht, insbesondere Urheber- und Datenschutzrecht, bei der Nutzung der Plattform einzuhalten
d) hochgeladene Inhalte auf Rechtmäßigkeit zu prüfen, insbesondere hinsichtlich Urheberrecht und Lizenzkonformität
e) den Anbieter unverzüglich über Sicherheitsvorfälle oder den Missbrauch von Zugangsdaten zu informieren

(2) Der Kunde stellt sicher, dass die ihm zugewiesenen Nutzerlizenzen nur von autorisierten Mitarbeitern oder Beauftragten verwendet werden. Eine Weitergabe von Zugangsdaten an Dritte außerhalb der Organisation des Kunden ist unzulässig.

(3) Der Kunde ist für die Einhaltung der Nutzungsrichtlinien der Plattform verantwortlich, insbesondere hinsichtlich der Veröffentlichung wissenschaftlicher Inhalte und der Nutzung der KI-Funktionen.`,
  },

  // ── § 6 Nutzungsrechte und geistiges Eigentum (+ Co-Creation IP) ──
  {
    id: 6,
    title: '§ 6 Nutzungsrechte und geistiges Eigentum',
    content: `(1) Der Anbieter räumt dem Kunden für die Vertragslaufzeit ein nicht-ausschließliches, nicht übertragbares Recht zur Nutzung der Plattform im Rahmen des vereinbarten Leistungsumfangs ein.

(2) Alle Rechte an der Plattform, einschließlich der Software, Algorithmen, KI-Modelle, des Designs und der Dokumentation, verbleiben beim Anbieter. Der Vertrag begründet keinen Übergang von geistigem Eigentum.

(3) Inhalte, die der Kunde über die Plattform hochlädt oder erstellt, verbleiben im Eigentum des Kunden bzw. der jeweiligen Rechteinhaber. Der Kunde räumt dem Anbieter die für die Vertragserfüllung erforderlichen Nutzungsrechte ein (Hosting, Anzeige, Indexierung, KI-Analyse).

(4) Ergebnisse der KI-gestützten Analyse (z.B. automatisierte Reviews) stellen keine eigenständigen urheberrechtlich geschützten Werke dar. Der Kunde darf diese Ergebnisse im Rahmen seiner wissenschaftlichen Tätigkeit frei verwenden.

(5) Die Dekompilierung, Reverse-Engineering oder anderweitige Nachbildung der Plattform oder ihrer Bestandteile ist unzulässig, soweit nicht gesetzlich zwingend erlaubt.`,
  },

  // ── § 6a Co-Creation IP (conditional) ──
  {
    id: 60,
    title: '§ 6a Gemeinsam entwickelte Review-Kriterien',
    condition: (agb) => agb.contractTerms.customReviewCriteria,
    content: `(1) Soweit die Parteien im Rahmen der Zusammenarbeit individuelle Review-Kriterien, Bewertungsstandards oder Analyseparameter gemeinsam entwickeln (nachfolgend „Custom-Kriterien"), stehen die Rechte an diesen Custom-Kriterien {{contractTerms.customCriteriaIP}} zu.

(2) Unabhängig von der Rechtezuordnung gemäß Abs. 1 ist der Anbieter berechtigt, aus der Zusammenarbeit gewonnene anonymisierte und aggregierte Erkenntnisse zur Verbesserung der Plattform und ihrer KI-Modelle zu verwenden, sofern keine Rückschlüsse auf den Kunden, dessen Mitarbeiter oder konkrete Forschungsinhalte möglich sind.

(3) Die Custom-Kriterien werden ausschließlich innerhalb des Kundenkontos angewendet und sind für andere Kunden nicht einsehbar oder nutzbar.`,
  },

  // ── § 7 Vergütung und Zahlungsbedingungen (+ flexible Modelle) ──
  {
    id: 7,
    title: '§ 7 Vergütung und Zahlungsbedingungen',
    content: `(1) Die Vergütung richtet sich nach dem vereinbarten Preismodell ({{commercialTerms.billingModel}}). Die Vergütung beträgt {{commercialTerms.pricing}} netto.

(2) Rechnungen sind innerhalb von {{commercialTerms.paymentTermDays}} Tagen nach Rechnungseingang ohne Abzug zur Zahlung fällig.

(3) Alle Preise verstehen sich netto zuzüglich der jeweils geltenden gesetzlichen Umsatzsteuer.

(4) Bei Zahlungsverzug ist der Anbieter berechtigt, Verzugszinsen in Höhe von 9 Prozentpunkten über dem Basiszinssatz gemäß § 288 Abs. 2 BGB zu verlangen. Die Geltendmachung eines weitergehenden Verzugsschadens bleibt vorbehalten.

(5) Der Anbieter behält sich vor, die Vergütung nach Ablauf der initialen Vertragslaufzeit mit einer Ankündigungsfrist von drei Monaten zum Ende der jeweiligen Vertragslaufzeit anzupassen.`,
  },

  // ── § 8 Haftung und Freistellung (merged §8+§9) ──
  {
    id: 8,
    title: '§ 8 Haftung und Freistellung',
    content: `(1) Der Anbieter haftet unbeschränkt für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit, die auf einer Pflichtverletzung des Anbieters oder seiner Erfüllungsgehilfen beruhen, sowie für Schäden, die durch Vorsatz oder grobe Fahrlässigkeit verursacht wurden.

(2) Bei leicht fahrlässiger Verletzung vertragswesentlicher Pflichten (Kardinalpflichten) ist die Haftung des Anbieters auf den vertragstypischen, vorhersehbaren Schaden beschränkt. Die Haftung ist in diesen Fällen der Höhe nach auf {{contractTerms.liabilityCap}} begrenzt.

(3) **KI-Haftungsausschluss**: Die KI-gestützten Analysen und Reviews der Plattform sind automatisch generierte Ergebnisse und ersetzen keine fachliche Begutachtung durch qualifizierte Wissenschaftler. Der Anbieter übernimmt keine Gewähr für die Richtigkeit, Vollständigkeit oder Eignung dieser Ergebnisse für einen bestimmten Zweck. Die Nutzung der KI-Ergebnisse erfolgt auf eigenes Risiko des Kunden.

(4) Die Haftung für den Verlust von Daten ist auf den typischen Wiederherstellungsaufwand beschränkt, der bei regelmäßiger und gefahrentsprechender Anfertigung von Sicherungskopien durch den Kunden entstanden wäre.

(5) Eine weitergehende Haftung des Anbieters ist ausgeschlossen. Insbesondere haftet der Anbieter nicht für mittelbare Schäden, entgangenen Gewinn oder Ausfallschäden.

(6) Der Kunde stellt den Anbieter von sämtlichen Ansprüchen Dritter frei, die auf einer rechtswidrigen Nutzung der Plattform durch den Kunden oder dessen Nutzer beruhen. Dies umfasst insbesondere Ansprüche wegen Verletzung von Urheberrechten, Persönlichkeitsrechten oder Datenschutzvorschriften.

(7) Der Kunde übernimmt die angemessenen Kosten der notwendigen Rechtsverteidigung des Anbieters einschließlich aller Gerichts- und Anwaltskosten in gesetzlicher Höhe. Dies gilt nicht, soweit der Anbieter die Rechtsverletzung zu vertreten hat.

(8) Der Kunde verpflichtet sich, den Anbieter unverzüglich über geltend gemachte Ansprüche Dritter zu informieren und alle für die Verteidigung erforderlichen Informationen und Unterlagen zur Verfügung zu stellen.`,
  },

  // ── § 9 Datenschutz ──
  {
    id: 9,
    title: '§ 9 Datenschutz',
    content: `(1) Der Anbieter verarbeitet personenbezogene Daten des Kunden und seiner Nutzer ausschließlich im Rahmen der geltenden Datenschutzvorschriften, insbesondere der Datenschutz-Grundverordnung (DSGVO) und des Bundesdatenschutzgesetzes (BDSG).

(2) Soweit der Anbieter im Rahmen der Vertragserfüllung personenbezogene Daten im Auftrag des Kunden verarbeitet, schließen die Parteien einen gesonderten Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO ab. Der AVV ist als Anlage Bestandteil dieses Vertrags.

(3) Einzelheiten zur Datenverarbeitung sind in der Datenschutzerklärung des Anbieters unter https://paperverse.io/privacy-policy beschrieben.

(4) Die technischen und organisatorischen Maßnahmen (TOMs) des Anbieters zum Schutz personenbezogener Daten sind im AVV (Anlage 1) dokumentiert.`,
  },

  // ── § 10 Geheimhaltung ──
  {
    id: 10,
    title: '§ 10 Geheimhaltung',
    content: `(1) Die Parteien verpflichten sich, alle im Rahmen der Vertragsanbahnung und -durchführung erlangten vertraulichen Informationen der jeweils anderen Partei zeitlich unbefristet vertraulich zu behandeln und nur für die Zwecke der Vertragsdurchführung zu verwenden.

(2) Als vertraulich gelten insbesondere:
a) Geschäftsgeheimnisse und Know-how
b) Unveröffentlichte wissenschaftliche Manuskripte und Forschungsdaten
c) Technische Dokumentation und API-Spezifikationen
d) Preis- und Konditionsinformationen
e) Informationen über Geschäftsprozesse und -strategien

(3) Die Geheimhaltungspflicht gilt nicht für Informationen, die:
a) zum Zeitpunkt der Mitteilung bereits öffentlich bekannt waren
b) nach der Mitteilung ohne Verschulden der empfangenden Partei öffentlich bekannt werden
c) der empfangenden Partei bereits vor der Mitteilung rechtmäßig bekannt waren
d) von einem Dritten ohne Verstoß gegen Geheimhaltungspflichten offengelegt werden
e) aufgrund gesetzlicher Verpflichtung offengelegt werden müssen

(4) Für diesen Vertrag gilt {{contractTerms.ndaLevel}} Geheimhaltungsschutz. Bei erweitertem Standard erstreckt sich der Schutz ausdrücklich auf alle über die Plattform zugänglich gemachten unveröffentlichten Forschungsdaten, Manuskriptentwürfe und Zwischenergebnisse des Kunden. Der Anbieter trifft hierfür zusätzliche technische Schutzmaßnahmen (verschlüsselte Speicherung, eingeschränkte Zugriffsrechte).`,
  },

  // ── § 11 Schlussbestimmungen (merged §12+§13+§14 + Force Majeure) ──
  {
    id: 11,
    title: '§ 11 Schlussbestimmungen',
    content: `(1) Bei Vertragsbeendigung stellt der Anbieter dem Kunden die auf der Plattform gespeicherten Inhalte in einem gängigen Format (PDF, CSV) zum Download bereit. Der Download-Zeitraum beträgt 30 Tage nach Vertragsende. Nach Ablauf werden die Inhalte des Kunden endgültig gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen. Der Anbieter bestätigt die vollständige Löschung auf Verlangen schriftlich.

(2) Öffentlich zugänglich gemachte Publikationen (mit Zustimmung des Kunden veröffentlichte Papers) können im Ermessen des Anbieters auf der Plattform verbleiben, sofern dies dem Zweck der wissenschaftlichen Verbreitung dient und der Kunde nicht ausdrücklich widerspricht.

(3) Der Anbieter behält sich vor, diese AGB mit Wirkung für die Zukunft zu ändern, soweit dies aus triftigen Gründen erforderlich ist (z.B. Gesetzesänderungen, neue Rechtsprechung, technische Weiterentwicklung). Änderungen werden dem Kunden mindestens sechs Wochen vor Inkrafttreten in Textform mitgeteilt.

(4) **Höhere Gewalt (Force Majeure)**: Keine Partei haftet für die Nichterfüllung oder verspätete Erfüllung ihrer Pflichten, soweit dies auf Umstände zurückzuführen ist, die außerhalb ihrer zumutbaren Kontrolle liegen (höhere Gewalt). Hierzu zählen insbesondere Naturkatastrophen, Pandemien, Cyberangriffe, behördliche Anordnungen, Ausfälle der Telekommunikationsinfrastruktur oder Stromversorgung. Die betroffene Partei informiert die andere Partei unverzüglich über den Eintritt und die voraussichtliche Dauer der höheren Gewalt. Dauert der Zustand der höheren Gewalt länger als drei Monate, hat jede Partei das Recht, den Vertrag außerordentlich zu kündigen.

(5) Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts (CISG).

(6) Gerichtsstand für alle Streitigkeiten aus oder im Zusammenhang mit diesem Vertrag ist Dresden, sofern der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder ein öffentlich-rechtliches Sondervermögen ist.

(7) Änderungen und Ergänzungen dieses Vertrags bedürfen der Schriftform. Dies gilt auch für die Aufhebung des Schriftformerfordernisses.

(8) Sollten einzelne Bestimmungen dieses Vertrags unwirksam oder undurchführbar sein oder werden, so wird die Wirksamkeit der übrigen Bestimmungen davon nicht berührt. Die Parteien verpflichten sich, die unwirksame oder undurchführbare Bestimmung durch eine wirksame Bestimmung zu ersetzen, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung möglichst nahekommt.

(9) Mündliche Nebenabreden bestehen nicht. Alle Anlagen sind Bestandteil dieses Vertrags.`,
  },
];

/**
 * Returns the effective clause list for a given AGB, filtering conditional clauses
 * and injecting the correct auto-renewal / fixed-end-date paragraph into § 3.
 */
export function getEffectiveClauses(agb: SavedAGB): AGBClause[] {
  const renewalParagraph = agb.contractTerms.autoRenewal
    ? `\n\n(4) Der Vertrag verlängert sich automatisch um jeweils weitere {{contractTerms.duration}}, sofern er nicht mit einer Frist von {{contractTerms.noticePeriod}} vor Ablauf der jeweiligen Vertragslaufzeit von einer Partei schriftlich gekündigt wird.`
    : `\n\n(4) Der Vertrag endet am {{contractTerms.fixedEndDate}}, ohne dass es einer Kündigung bedarf. Eine automatische Vertragsverlängerung findet nicht statt. Die Parteien können vor Vertragsende einvernehmlich eine Verlängerung vereinbaren.`;

  const consentParagraph = agb.contractTerms.activeConsentForChanges
    ? `\n\n(3a) Abweichend von Abs. 3 Satz 2 bedürfen Änderungen dieser AGB gegenüber dem Kunden der ausdrücklichen schriftlichen Zustimmung. Ein bloßes Schweigen gilt nicht als Zustimmung.`
    : `\n\n(3a) Die Änderungen gelten als genehmigt, wenn der Kunde nicht innerhalb von sechs Wochen nach Zugang der Änderungsmitteilung schriftlich widerspricht. Im Falle eines Widerspruchs haben beide Parteien das Recht, den Vertrag mit einer Frist von drei Monaten zum Ende der laufenden Vertragslaufzeit zu kündigen.`;

  // Model-specific billing detail paragraph for § 7
  const billingModel = agb.commercialTerms.billingModel;
  let billingDetailParagraph = '';
  if (billingModel === 'one_time_per_user') {
    billingDetailParagraph = `\n\n(5) Die Vergütung wird als Einmalzahlung in Höhe von {{commercialTerms.pricePerUser}} pro Nutzer erhoben. Die Zahlung ist mit Bereitstellung der Nutzerlizenzen fällig.`;
  } else if (billingModel === 'credits_plus_fixed') {
    billingDetailParagraph = `\n\n(5) Die Vergütung setzt sich aus einem Fixbetrag von {{commercialTerms.fixedFee}} sowie einem nutzungsabhängigen Anteil zusammen. Im Fixbetrag sind {{commercialTerms.creditsIncluded}} KI-Review-Credits enthalten. Darüber hinausgehende Credits werden gesondert berechnet.`;
  } else if (billingModel === 'cooperation') {
    billingDetailParagraph = `\n\n(5) Im Rahmen des Kooperationsmodells beschränkt sich die Vergütung auf die technologischen Kosten für die KI-gestützte Verarbeitung ({{commercialTerms.cooperationCostBasis}}). Eine darüber hinausgehende Plattformgebühr wird nicht erhoben.`;
  }

  // Price adjustment clause — only for recurring billing models
  const priceAdjustmentParagraph = (billingModel === 'monthly' || billingModel === 'yearly' || billingModel === 'credits_plus_fixed')
    ? `\n\n(${billingDetailParagraph ? '6' : '5'}) Der Anbieter behält sich vor, die Vergütung nach Ablauf der initialen Vertragslaufzeit mit einer Ankündigungsfrist von drei Monaten zum Ende der jeweiligen Vertragslaufzeit anzupassen.`
    : '';

  // Step 1: Filter conditional clauses
  const filtered = agbClauses.filter(clause => !clause.condition || clause.condition(agb));

  // Step 2: Apply content transformations
  const transformed = filtered.map(clause => {
    // Inject renewal paragraph into § 3 + handle fixed end date in Abs. 2
    if (clause.id === 3) {
      let content = clause.content;
      if (!agb.contractTerms.autoRenewal && agb.contractTerms.fixedEndDate) {
        content = content.replace(
          '(2) Der Vertrag beginnt am {{contractTerms.startDate}} und hat eine Laufzeit von {{contractTerms.duration}}.',
          '(2) Der Vertrag beginnt am {{contractTerms.startDate}} und endet am {{contractTerms.fixedEndDate}}.'
        );
      }
      return { ...clause, content: content + renewalParagraph };
    }
    // Conditionally remove API references from § 1 and § 2
    if (clause.id === 1 && !agb.serviceScope.apiAccess) {
      return { ...clause, content: clause.content.replace(' sowie zugehöriger API-Dienste', '') };
    }
    if (clause.id === 2 && !agb.serviceScope.apiAccess) {
      let content = clause.content
        .replace('\ne) API-Zugang für programmatische Integration', '')
        .replace(
          '\nc) **API-Zugang**: Programmatischer Zugang mit einem Rate-Limit von {{serviceScope.apiRateLimit}}',
          ''
        );
      // Fix letter gap: rename d) → c) in Abs. 2 after removing c)
      content = content.replace(
        '\nd) **Support**: {{contractTerms.supportLevel}}',
        '\nc) **Support**: {{contractTerms.supportLevel}}'
      );
      return { ...clause, content };
    }
    // Replace § 7 Abs. 5 with model-specific details + conditional price adjustment
    if (clause.id === 7) {
      let content = clause.content.replace(
        '\n\n(5) Der Anbieter behält sich vor, die Vergütung nach Ablauf der initialen Vertragslaufzeit mit einer Ankündigungsfrist von drei Monaten zum Ende der jeweiligen Vertragslaufzeit anzupassen.',
        ''
      );
      // For cooperation model, replace Abs. 1 pricing sentence with reference to Abs. 5
      if (billingModel === 'cooperation') {
        content = content.replace(
          '(1) Die Vergütung richtet sich nach dem vereinbarten Preismodell ({{commercialTerms.billingModel}}). Die Vergütung beträgt {{commercialTerms.pricing}} netto.',
          '(1) Die Vergütung richtet sich nach dem nachfolgend vereinbarten Kooperationsmodell ({{commercialTerms.billingModel}}). Einzelheiten ergeben sich aus Abs. 5.'
        );
      }
      return { ...clause, content: content + billingDetailParagraph + priceAdjustmentParagraph };
    }
    // Conditionally remove "und API-Spezifikationen" from § 10 when no API access
    if (clause.id === 10 && !agb.serviceScope.apiAccess) {
      return { ...clause, content: clause.content.replace(' und API-Spezifikationen', '') };
    }
    // Inject consent paragraph into § 11 — AFTER Abs. (3)
    if (clause.id === 11) {
      const marker = '(3) Der Anbieter behält sich vor, diese AGB mit Wirkung für die Zukunft zu ändern, soweit dies aus triftigen Gründen erforderlich ist (z.B. Gesetzesänderungen, neue Rechtsprechung, technische Weiterentwicklung). Änderungen werden dem Kunden mindestens sechs Wochen vor Inkrafttreten in Textform mitgeteilt.';
      const content = clause.content.replace(marker, marker + consentParagraph);
      return { ...clause, content };
    }
    return clause;
  });

  // Step 3: Renumber paragraphs sequentially and fix cross-references
  // Build a mapping from original § number to new § number
  const originalNumbers: Map<number, number> = new Map();
  transformed.forEach((clause, index) => {
    // Extract original number from title (e.g. "§ 4" → 4, "§ 6a" → 60)
    originalNumbers.set(clause.id, index + 1);
  });

  // Find the new number for the Schlussbestimmungen (originally § 11)
  const schlussNumber = originalNumbers.get(11) ?? transformed.length;

  return transformed.map((clause, index) => {
    const newNumber = index + 1;
    // Replace § number in title (handles both "§ 6a" and "§ 4" patterns)
    const titleWithoutNumber = clause.title.replace(/^§\s*\d+[a-z]?\s+/, '');
    const newTitle = `§ ${newNumber} ${titleWithoutNumber}`;

    // Fix internal cross-reference: § 4 → § 11 Abs. 1 (only exists when tenantIsolation=true)
    let content = clause.content;
    if (clause.id === 4) {
      content = content.replace(
        `gemäß § 11 Abs. 1 dieses Vertrags`,
        `gemäß § ${schlussNumber} Abs. 1 dieses Vertrags`
      );
    }

    return { ...clause, title: newTitle, content };
  });
}

export function getIncompleteAGBFields(agb: SavedAGB): string[] {
  const missing: string[] = [];
  if (!agb.customer.name) missing.push('Kundenname');
  if (!agb.customer.address) missing.push('Kundenadresse');
  if (!agb.customer.representedBy) missing.push('Vertretungsberechtigter');
  if (!agb.commercialTerms.pricing && agb.commercialTerms.billingModel !== 'cooperation') missing.push('Vergütung');
  if (!agb.contractTerms.startDate) missing.push('Vertragsbeginn');
  if (!agb.contractTerms.autoRenewal && !agb.contractTerms.fixedEndDate) missing.push('Festes Enddatum');
  if (agb.contractTerms.autoRenewal && !agb.contractTerms.noticePeriod) missing.push('Kündigungsfrist');
  if (agb.contractTerms.liabilityCapType === 'fixed' && !agb.contractTerms.liabilityCap) missing.push('Haftungsbetrag');
  // Model-specific required fields
  const model = agb.commercialTerms.billingModel;
  if (model === 'one_time_per_user' && !agb.commercialTerms.pricePerUser) missing.push('Preis pro Nutzer');
  if (model === 'credits_plus_fixed') {
    if (!agb.commercialTerms.fixedFee) missing.push('Fixbetrag');
    if (!agb.commercialTerms.creditsIncluded) missing.push('Inkludierte Credits');
  }
  if (model === 'cooperation' && !agb.commercialTerms.cooperationCostBasis) missing.push('Kostenbasis (Kooperation)');
  return missing;
}

export function generateAGBTitle(customerName: string): string {
  return `zwischen ${agbProvider.name} und ${customerName || '[Kundenname]'}`;
}

/** Migrate old AGBs that use billingCycle to new billingModel format */
export function migrateAGB(agb: any): SavedAGB {
  const ct = agb.contractTerms || {};
  const cm = agb.commercialTerms || {};
  return {
    ...agb,
    commercialTerms: {
      ...cm,
      billingModel: cm.billingModel || cm.billingCycle || 'yearly',
      pricePerUser: cm.pricePerUser || '',
      fixedFee: cm.fixedFee || '',
      creditsIncluded: cm.creditsIncluded || '',
      cooperationCostBasis: cm.cooperationCostBasis || '',
    },
    contractTerms: {
      ...ct,
      autoRenewal: ct.autoRenewal ?? true,
      fixedEndDate: ct.fixedEndDate || '',
      tenantIsolation: ct.tenantIsolation ?? false,
      customReviewCriteria: ct.customReviewCriteria ?? false,
      customCriteriaIP: ct.customCriteriaIP || 'shared',
      activeConsentForChanges: ct.activeConsentForChanges ?? false,
    },
  };
}
